import React, { useEffect } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ServiceHero from './components/ServiceHero';
import ServiceCard from './components/ServiceCard';
import TechnologyStack from './components/TechnologyStack';
import ProcessOverview from './components/ProcessOverview';
import IndustryExpertise from './components/IndustryExpertise';
import CaseStudyPreview from './components/CaseStudyPreview';
import PricingGuide from './components/PricingGuide';
import FAQSection from './components/FAQSection';
import ContactCTA from './components/ContactCTA';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: 'MVP Development',
      description: 'Transform your innovative idea into a market-ready minimum viable product. We help startups validate concepts quickly with core features, user feedback loops, and scalable architecture foundation.',
      icon: 'Rocket',
      bgColor: 'bg-blue-100',
      iconColor: '#3182ce',
      category: 'Startup',
      feature1: 'Rapid prototyping and iterative development',
      feature2: 'Core feature implementation with user testing',
      feature3: 'Cloud deployment with monitoring setup',
      pricing: '$25,000'
    },
    {
      title: 'Full-Stack Development',
      description: 'Comprehensive end-to-end development services covering frontend, backend, database design, and API integration. Build robust, scalable applications with modern technology stacks.',
      icon: 'Code2',
      bgColor: 'bg-purple-100',
      iconColor: '#7c3aed',
      category: 'Development',
      feature1: 'Modern frontend with React/Next.js',
      feature2: 'Scalable backend architecture',
      feature3: 'RESTful and GraphQL API development',
      pricing: '$50,000'
    },
    {
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android. Deliver seamless user experiences with offline capabilities, push notifications, and device integration.',
      icon: 'Smartphone',
      bgColor: 'bg-green-100',
      iconColor: '#059669',
      category: 'Mobile',
      feature1: 'Cross-platform with React Native/Flutter',
      feature2: 'Native iOS and Android development',
      feature3: 'App store optimization and deployment',
      pricing: '$40,000'
    },
    {
      title: 'Cloud Architecture',
      description: 'Design and implement scalable cloud infrastructure on AWS, Google Cloud, or Azure. Optimize performance, security, and cost-efficiency with modern DevOps practices.',
      icon: 'Cloud',
      bgColor: 'bg-orange-100',
      iconColor: '#ea580c',
      category: 'Infrastructure',
      feature1: 'Multi-cloud deployment strategies',
      feature2: 'Auto-scaling and load balancing',
      feature3: 'CI/CD pipeline implementation',
      pricing: '$30,000'
    },
    {
      title: 'API Development',
      description: 'Build secure, well-documented APIs that power your applications and enable third-party integrations. RESTful and GraphQL APIs with comprehensive documentation.',
      icon: 'Network',
      bgColor: 'bg-pink-100',
      iconColor: '#db2777',
      category: 'Integration',
      feature1: 'RESTful and GraphQL API design',
      feature2: 'API security and authentication',
      feature3: 'Comprehensive API documentation',
      pricing: '$20,000'
    },
    {
      title: 'UI/UX Design',
      description: 'Create intuitive, beautiful user interfaces backed by user research and data-driven design decisions. From wireframes to high-fidelity prototypes and design systems.',
      icon: 'Palette',
      bgColor: 'bg-yellow-100',
      iconColor: '#d97706',
      category: 'Design',
      feature1: 'User research and persona development',
      feature2: 'Interactive prototypes and testing',
      feature3: 'Design system and component library',
      pricing: '$15,000'
    },
    {
      title: 'Database Design',
      description: 'Architect efficient, scalable database solutions optimized for your data model. SQL and NoSQL databases with performance tuning and migration strategies.',
      icon: 'Database',
      bgColor: 'bg-indigo-100',
      iconColor: '#4f46e5',
      category: 'Data',
      feature1: 'Relational and NoSQL database design',
      feature2: 'Performance optimization and indexing',
      feature3: 'Data migration and backup strategies',
      pricing: '$18,000'
    },
    {
      title: 'DevOps & Automation',
      description: 'Streamline development workflows with automated testing, continuous integration, and deployment pipelines. Infrastructure as code and monitoring solutions.',
      icon: 'GitBranch',
      bgColor: 'bg-teal-100',
      iconColor: '#0d9488',
      category: 'Operations',
      feature1: 'CI/CD pipeline setup and automation',
      feature2: 'Infrastructure as code (Terraform)',
      feature3: 'Monitoring and alerting systems',
      pricing: '$25,000'
    },
    {
      title: 'Security & Compliance',
      description: 'Implement enterprise-grade security measures and ensure compliance with industry standards. Security audits, penetration testing, and compliance certification support.',
      icon: 'Shield',
      bgColor: 'bg-red-100',
      iconColor: '#dc2626',
      category: 'Security',
      feature1: 'Security audits and penetration testing',
      feature2: 'GDPR, HIPAA, SOC 2 compliance',
      feature3: 'Encryption and access control',
      pricing: '$35,000'
    },
    {
      title: 'Technical Consulting',
      description: 'Strategic technology guidance for complex projects. Architecture reviews, technology stack selection, and technical due diligence for investments.',
      icon: 'Lightbulb',
      bgColor: 'bg-cyan-100',
      iconColor: '#0891b2',
      category: 'Consulting',
      feature1: 'Technology stack recommendations',
      feature2: 'Architecture design and review',
      feature3: 'Technical due diligence',
      pricing: '$200/hour'
    },
    {
      title: 'Legacy Modernization',
      description: 'Transform outdated systems into modern, maintainable applications. Gradual migration strategies that minimize business disruption while maximizing value.',
      icon: 'RefreshCw',
      bgColor: 'bg-lime-100',
      iconColor: '#65a30d',
      category: 'Migration',
      feature1: 'Legacy system assessment',
      feature2: 'Phased migration strategy',
      feature3: 'Modern technology stack adoption',
      pricing: '$60,000'
    },
    {
      title: 'Ongoing Maintenance',
      description: 'Comprehensive post-launch support including bug fixes, performance optimization, security updates, and feature enhancements. Keep your application running smoothly.',
      icon: 'Settings',
      bgColor: 'bg-violet-100',
      iconColor: '#7c3aed',
      category: 'Support',
      feature1: '24/7 monitoring and support',
      feature2: 'Regular updates and patches',
      feature3: 'Performance optimization',
      pricing: '$5,000/month'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumbs />
        </div> */}

        <ServiceHero />

        <section className="py-16 md:py-24 lg:py-32 bg-background" id="services-overview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
                Comprehensive Service Portfolio
              </h2>
              <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
                From concept to deployment and beyond, we provide end-to-end services that cover every aspect of SaaS development
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {services?.map((service, index) => (
                <ServiceCard key={index} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        <TechnologyStack />
        {/* <ProcessOverview /> */}
        {/* <IndustryExpertise /> */}
        {/* <CaseStudyPreview /> */}
        <PricingGuide />
        <FAQSection />
        <ContactCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Services;